import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, MonitorSmartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

const STATUS_TONE = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  revoked: 'bg-red-100 text-red-800 border-red-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
};

const EndpointDevices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ws = JSON.parse(localStorage.getItem('current_workspace') || 'null');
      const r = await axios.get('/api/endpoint/installations', { params: ws?.id ? { workspace_id: ws.id } : {} });
      setRows(r.data?.installations || []);
    } catch (e) {
      toast.error('Failed to load devices');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    setRevoking(id);
    try {
      await axios.post(`/api/endpoint/installations/${id}/revoke`);
      toast.success('Device revoked');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally { setRevoking(null); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="endpoint-devices">
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <MonitorSmartphone className="w-5 h-5 text-slate-700" />
            <CardTitle className="text-lg">Nova Endpoint devices</CardTitle>
          </div>
          <CardDescription>Installations authorized to run local scans and Cloud-powered code reviews for your account. Revoke to lock a device immediately.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-slate-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-slate-500 text-sm py-6" data-testid="devices-empty">No Endpoint installations yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((d) => (
                <div key={d.installation_id} className="flex items-center justify-between py-3" data-testid={`device-${d.installation_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{d.device_name || 'Nova Endpoint'}</span>
                      <Badge className={`text-[10px] border ${STATUS_TONE[d.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{d.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono truncate">{d.platform || 'unknown'} · {d.installation_id}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Created {fmt(d.created_at)} · Last seen {fmt(d.last_seen_at)}</div>
                  </div>
                  {d.status === 'active' && (
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => revoke(d.installation_id)} disabled={revoking === d.installation_id}
                      data-testid={`revoke-${d.installation_id}`}>
                      {revoking === d.installation_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                      Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EndpointDevices;
